import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Loader2, Command, Mic } from "lucide-react";
import { APPS } from "./apps";
import { useWM } from "./store";
import { aiQuick } from "./api";
import { useAuth } from "./AuthContext";
import { probeMic, voiceErrorText, micBlockerText } from "./voice";

const BUSINESS_ONLY_APPS = new Set(["attendance"]);

// Slash commands. Each returns the app id to open and optional initial props.
const COMMANDS = [
  { cmd: "/chat", desc: "Open HEXA Chat", run: ({ open }) => open(APPS.find(a => a.id === "chat")) },
  { cmd: "/new-mail", desc: "Compose a new mail", run: ({ open }) => open(APPS.find(a => a.id === "mail")) },
  { cmd: "/meet", desc: "New meeting", run: ({ open }) => open(APPS.find(a => a.id === "meetings")) },
  { cmd: "/calc", desc: "Open calculator", run: ({ open }) => open(APPS.find(a => a.id === "calculator")) },
  { cmd: "/img", desc: "Generate an image", run: ({ open }) => open(APPS.find(a => a.id === "imagegen")) },
  { cmd: "/note", desc: "Create a note", run: ({ open }) => open(APPS.find(a => a.id === "notes")) },
  { cmd: "/scan", desc: "Open Security & scan", run: ({ open }) => open(APPS.find(a => a.id === "security")) },
  { cmd: "/rec", desc: "Screen recorder", run: ({ open }) => open(APPS.find(a => a.id === "recorder")) },
  { cmd: "/tasks", desc: "Open Projects (tasks live inside)", run: ({ open }) => open(APPS.find(a => a.id === "projects")) },
  { cmd: "/browser", desc: "Open browser", run: ({ open }) => open(APPS.find(a => a.id === "browser")) },
  { cmd: "/calculator", desc: "Open calculator", run: ({ open }) => open(APPS.find(a => a.id === "calculator")) },
  { cmd: "/shortcuts", desc: "Show keyboard shortcuts", run: ({ open }) => open(APPS.find(a => a.id === "shortcuts")) },
  { cmd: "/quote", desc: "Open Quotation Maker", run: ({ open }) => open(APPS.find(a => a.id === "quotation")) },
  { cmd: "/crm", desc: "Open CRM", run: ({ open }) => open(APPS.find(a => a.id === "crm")) },
];

export default function Spotlight({ open: isOpen, onClose }) {
  const [q, setQ] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceMsg, setVoiceMsg] = useState("");
  const inputRef = useRef(null);
  const recogRef = useRef(null);
  const { open: openApp } = useWM();
  const { user, appAccess } = useAuth();
  const isIndividual = user?.role === "individual";

  useEffect(() => {
    if (isOpen) {
      setQ(""); setAiAnswer("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const lower = q.trim().toLowerCase();
  const isSlash = q.startsWith("/");
  const slashMatches = isSlash ? COMMANDS.filter(c => c.cmd.includes(lower)) : COMMANDS.filter(() => q === "");
  const appMatches = APPS
    .filter(a => !a.hideFromDock && a.title.toLowerCase().includes(lower))
    .filter(a => !(isIndividual && BUSINESS_ONLY_APPS.has(a.id)))
    .filter(a => !(a.requiresSuperAdmin && !user?.is_super_admin))
    .filter(a => !appAccess?.hidden?.includes(a.id));

  const runSlash = (c) => {
    c.run({ open: openApp });
    onClose();
  };

  const askAI = async () => {
    if (!q.trim()) return;
    if (q.startsWith("/")) {
      const c = COMMANDS.find(x => x.cmd === q.split(" ")[0]);
      if (c) { runSlash(c); return; }
    }
    setLoading(true); setAiAnswer("");
    try {
      const r = await aiQuick(q, "You are HEXA, the AI assistant inside HEXA Web OS. Answer concisely.");
      setAiAnswer(r.text);
    } catch { setAiAnswer("AI is unavailable right now."); }
    setLoading(false);
  };

  const onKey = (e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "Enter") askAI();
  };

  const startListening = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setVoiceMsg("Voice input needs Chrome or Edge on desktop."); return; }
    if (listening) { recogRef.current?.stop(); return; }

    setVoiceMsg("Checking microphone…");
    const mic = await probeMic();
    const blocker = micBlockerText(mic.verdict);
    if (blocker) { setVoiceMsg(blocker); return; }
    setVoiceMsg("");
    const deadStream = mic.verdict === "silent";
    let sawAnyResult = false;
    let retriedNoSpeech = false;

    const r = new SR();
    r.lang = "en-US"; r.continuous = false; r.interimResults = true;
    r.onstart = () => { setListening(true); setVoiceMsg(deadStream ? "Listening, but no sound is arriving…" : "Listening — speak now."); };
    r.onend = () => setListening(false);
    // Previously this swallowed every failure, so a blocked or silent mic looked
    // like nothing happening at all.
    r.onerror = (ev) => {
      const code = ev?.error || "unknown";
      if (code === "no-speech" && !sawAnyResult && !retriedNoSpeech && !deadStream) {
        retriedNoSpeech = true;
        setVoiceMsg("Still listening — speak now…");
        try { r.start(); return; } catch (_) { /* fall through */ }
      }
      setListening(false);
      if (code !== "aborted") setVoiceMsg(voiceErrorText(code, { deadStream }));
    };
    r.onresult = (ev) => {
      let t = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) t += ev.results[i][0].transcript;
      if (t) { sawAnyResult = true; setVoiceMsg(""); }
      setQ(t);
      if (ev.results[ev.results.length - 1].isFinal) {
        setTimeout(askAI, 200);
      }
    };
    recogRef.current = r;
    r.start();
  };

  return (
    <div
      data-testid="spotlight-overlay"
      className="fixed inset-0 z-[1000] flex items-start justify-center pt-[14vh]"
      style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="w-[640px] max-w-[92vw] panel-strong overflow-hidden float-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(139,92,246,0.18) inset",
        }}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#8b5cf6]/15">
          <span
            className="inline-block"
            style={{
              width: 16, height: 18,
              clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
              background: "linear-gradient(160deg, #8b5cf6, #0891b2)",
            }}
          />
          <input
            data-testid="spotlight-input"
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="ask hexa · search apps · / for commands"
            className="flex-1 bg-transparent outline-none text-base placeholder-[#c7c1ff]/40 mono"
          />
          <button data-testid="spotlight-mic" onClick={startListening} className={`btn-ghost p-2 ${listening ? "text-[#3b82f6] pulse-amber" : ""}`} title="Voice search">
            <Mic size={14} />
          </button>
          <button onClick={askAI} data-testid="spotlight-ask-ai" className="btn-accent text-[10px] uppercase tracking-widest flex items-center gap-1 mono">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            run
          </button>
        </div>

        {voiceMsg && (
          <div
            data-testid="spotlight-voice-msg"
            className="px-4 py-2 text-[11px] text-[#fbbf24] leading-snug border-b border-[#8b5cf6]/15"
          >
            {voiceMsg}
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto">
          {(isSlash || q === "") && slashMatches.length > 0 && (
            <div className="p-2">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 px-3 py-2 flex items-center gap-1 mono"><Command size={10} /> commands</div>
              {slashMatches.map(c => (
                <button
                  key={c.cmd}
                  data-testid={`spotlight-cmd-${c.cmd.slice(1)}`}
                  onClick={() => runSlash(c)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#8b5cf6]/10 text-left"
                >
                  <span className="mono text-[#3b82f6] text-xs w-24 uppercase tracking-wider">{c.cmd}</span>
                  <span className="text-sm text-[#e8f4f8]/90">{c.desc}</span>
                </button>
              ))}
            </div>
          )}
          {!isSlash && appMatches.length > 0 && (
            <div className="p-2 border-t border-[#8b5cf6]/15">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#c7c1ff]/50 px-3 py-2 mono">apps</div>
              {appMatches.map(app => {
                const Icon = app.icon;
                return (
                  <button
                    key={app.id}
                    data-testid={`spotlight-app-${app.id}`}
                    onClick={() => { openApp(app); onClose(); }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-[#8b5cf6]/10 text-left"
                  >
                    <span
                      className="inline-flex items-center justify-center"
                      style={{
                        width: 26, height: 30,
                        clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
                        background: "rgba(139,92,246,0.15)",
                      }}
                    >
                      <Icon size={14} strokeWidth={1.6} color="#c7c1ff" />
                    </span>
                    <div className="flex-1">
                      <div className="text-sm mono">{app.title}</div>
                      <div className="text-[11px] text-[#c7c1ff]/50">{app.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {aiAnswer && (
            <div className="border-t border-[#8b5cf6]/15 p-4">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#3b82f6] mb-2 mono">// hexa.response</div>
              <div className="text-sm text-[#e8f4f8] whitespace-pre-wrap">{aiAnswer}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
